import { LitElement, html, css } from 'lit'
import { APP_VERSION } from '../config/version.js'
import { API_CONFIG } from '../config/api.config.js'

/**
 * Constante para a chave do cache no sessionStorage
 * Utilizada para armazenar a versão do backend com timestamp
 */
const CACHE_KEY = 'equilibra_backend_version'

/**
 * TTL do cache em milissegundos (10 minutos)
 * Após esse período, uma nova requisição ao backend será feita
 */
const CACHE_TTL = 600000

/**
 * Timeout para requisição ao backend em milissegundos (5 segundos)
 * Se o backend não responder nesse tempo, exibe apenas versão frontend
 */
const FETCH_TIMEOUT = 5000

/**
 * Componente que exibe a versão do frontend e opcionalmente do backend
 * Utiliza cache em sessionStorage com TTL de 10 minutos para evitar
 * requisições desnecessárias ao endpoint /api/version
 *
 * Comportamento:
 * - Ao conectar, exibe a versão do frontend imediatamente
 * - Tenta carregar a versão do backend (do cache ou via fetch)
 * - Se backend disponível: "Frontend: vX.Y.Z | API: vA.B.C"
 * - Se backend indisponível: "vX.Y.Z" (apenas frontend)
 * - Fallback "v0.0.0" se versão do frontend não disponível
 *
 * @element version-display
 */
class VersionDisplay extends LitElement {
  static properties = {
    /** Versão do frontend no formato SemVer (sem prefixo "v") */
    frontendVersion: { type: String },
    /** Versão do backend no formato SemVer (sem prefixo "v"), null se indisponível */
    backendVersion: { type: String },
    /** Indica se está carregando a versão do backend */
    loading: { type: Boolean }
  }

  /**
   * Estilos do componente utilizando tokens do Design System
   * - font-size: 0.75rem conforme requisito 4.4
   * - cor: --text-secondary para contraste WCAG AA 4.5:1
   */
  static styles = css`
    :host {
      display: block;
      text-align: center;
      padding: 1rem 0;
    }
    .version-text {
      font-size: 0.75rem;
      color: var(--text-secondary, #A8B3CF);
    }
  `

  constructor () {
    super()
    this.frontendVersion = null
    this.backendVersion = null
    this.loading = false
  }

  /**
   * Lifecycle callback do Lit — executado quando o componente é inserido no DOM
   * Inicializa a versão do frontend e dispara o carregamento da versão do backend
   */
  connectedCallback () {
    super.connectedCallback()
    this.frontendVersion = this._getFrontendVersion()
    this._loadBackendVersion()
  }

  /**
   * Obtém a versão do frontend a partir do módulo de configuração
   * Retorna "0.0.0" como fallback se a versão não estiver disponível
   * @returns {string} Versão no formato SemVer (ex: "2.0.0")
   */
  _getFrontendVersion () {
    if (APP_VERSION && APP_VERSION.version) {
      return APP_VERSION.version
    }
    return '0.0.0'
  }

  /**
   * Carrega a versão do backend, verificando primeiro o cache em sessionStorage
   * Se o cache for válido (< 10 min), usa o valor cacheado
   * Se inválido ou ausente, faz requisição GET /api/version com timeout de 5s
   * Em caso de falha, mantém backendVersion como null (exibe apenas frontend)
   */
  async _loadBackendVersion () {
    this.loading = true

    try {
      // Tenta usar cache do sessionStorage
      const cached = this._getCachedVersion()
      if (cached) {
        this.backendVersion = cached
        this.loading = false
        return
      }

      // Cache inválido ou ausente — consulta o backend
      const version = await this._fetchBackendVersion()
      if (version) {
        this.backendVersion = version
        this._setCachedVersion(version)
      }
    } catch {
      // Falha silenciosa — exibe apenas versão frontend
    } finally {
      this.loading = false
    }
  }

  /**
   * Verifica se existe cache válido no sessionStorage
   * O cache é considerado válido se foi gravado há menos de 10 minutos
   * @returns {string|null} Versão do backend cacheada ou null se inválido/ausente
   */
  _getCachedVersion () {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY)
      if (!raw) return null

      const cached = JSON.parse(raw)
      if (!cached || !cached.data || !cached.timestamp) return null

      const age = Date.now() - cached.timestamp
      if (age >= CACHE_TTL) return null

      return cached.data.version || null
    } catch {
      // sessionStorage indisponível ou JSON inválido — retorna null
      return null
    }
  }

  /**
   * Grava a versão do backend no sessionStorage com timestamp atual
   * Se sessionStorage não estiver disponível, falha silenciosamente
   * @param {string} version - Versão do backend no formato SemVer
   */
  _setCachedVersion (version) {
    try {
      const cacheData = {
        data: { version },
        timestamp: Date.now()
      }
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData))
    } catch {
      // sessionStorage indisponível — ignora silenciosamente
    }
  }

  /**
   * Faz requisição GET ao endpoint /api/version com timeout de 5 segundos
   * Utiliza AbortController para implementar o timeout
   * @returns {string|null} Versão do backend ou null em caso de falha
   */
  async _fetchBackendVersion () {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT)

    try {
      const url = `${API_CONFIG.BASE_URL}/version`
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) return null

      const data = await response.json()
      if (!data || !data.version) return null

      return data.version
    } catch {
      clearTimeout(timeoutId)
      // Timeout, erro de rede ou JSON inválido — retorna null
      return null
    }
  }

  /**
   * Renderiza o componente com a versão do frontend e opcionalmente do backend
   * Formato com backend: "Frontend: vX.Y.Z | API: vA.B.C"
   * Formato sem backend: "vX.Y.Z"
   * @returns {import('lit').TemplateResult} Template HTML do componente
   */
  render () {
    const frontendDisplay = `v${this.frontendVersion || '0.0.0'}`

    if (this.backendVersion) {
      return html`
        <div class="version-text">
          <span data-cy="app-version">Frontend: ${frontendDisplay}</span>
          <span> | </span>
          <span data-cy="api-version">API: v${this.backendVersion}</span>
        </div>
      `
    }

    return html`
      <div class="version-text">
        <span data-cy="app-version">${frontendDisplay}</span>
      </div>
    `
  }
}

customElements.define('version-display', VersionDisplay)
